import React, { ReactNode } from 'react';
import {
    Filter,
    TextInput,
    ReferenceInput,
    AutocompleteInput,
    SelectInput,
} from 'react-admin';
import CustomizableGrid from '../components/CustomizableGrid';
import { ColumnConfig } from '../hooks/useColumnReorder';
import {
    Datagrid,
    TextField,
    DateField,
    EditButton,
    ShowButton,
    ReferenceField,
    FunctionField,
    Link,
    ChipField,
} from 'react-admin';
import UserReferenceField from '../components/UserReferenceField';
import CityField from '../components/CityField';
import DeleteButton from '../components/DeleteButton';
import CustomSearchInput from '../components/CustomSearchInput';
import AddressGrid from './components/AddressGrid';
import BoxitPagination from '../components/BoxitPagination';

// Custom field components for clickable links
const CustomerField = ({ record, ...props }) => {
    if (!record || !record.user) return null;
    return (
        <Link to={`/users/${record.userId}/show`} {...props}>
            {record.user.name}
        </Link>
    );
};

const PartnerField = ({ record, ...props }) => {
    if (!record || !record.partner) return null;
    return (
        <Link to={`/partners/${record.partnerId}/show`} {...props}>
            {record.partner.name}
        </Link>
    );
};

const CustomerTypeField = ({ record, ...props }) => {
    if (!record || !record.user) return null;
    const color =
        record.user.type === 'b2c'
            ? 'primary'
            : record.user.type === 'sme'
            ? 'secondary'
            : 'default';
    return <ChipField source="user.type" color={color} {...props} />;
};
const AddressFilter = props => {
    return (
        <Filter variant="outlined" {...props}>
            <CustomSearchInput alwaysOn />
            <ReferenceInput
                source="partnerId"
                reference="partners"
                resource="partners"
                label="Partner"
            >
                <SelectInput />
            </ReferenceInput>

            <ReferenceInput
                source="userId"
                reference="users"
                resource="users"
                label="Customer"
            >
                <AutocompleteInput
                    shouldRenderSuggestions={val => val.trim().length > 2}
                    optionText={user => (user.name ? user.name : user.email)}
                />
            </ReferenceInput>

            <ReferenceInput
                source="cityId"
                reference="city"
                resource="city"
                label="City"
            >
                <SelectInput />
            </ReferenceInput>
        </Filter>
    );
};

const AddressList = ({ permissions = [], ...props }) => {
    // Define default columns configuration
    const defaultColumns: ColumnConfig[] = [
        // Default visible columns (essential information)
        { id: 'serial', label: 'Serial', visible: true, order: 0 },
        { id: 'id', label: 'ID', visible: true, order: 1 },
        { id: 'name', label: 'Name', visible: true, order: 2 },
        { id: 'line1', label: 'Line', visible: true, order: 3 },
        { id: 'area', label: 'Area', visible: true, order: 4 },
        { id: 'customer', label: 'Customer', visible: true, order: 5 },
        { id: 'partner', label: 'Partner', visible: true, order: 6 },
        { id: 'city', label: 'City', visible: true, order: 7 },
        { id: 'country', label: 'Country', visible: true, order: 8 },
        { id: 'show', label: 'Show', visible: true, order: 9 },

        // Optional columns (can be enabled by users)
        { id: 'createdAt', label: 'Created At', visible: false, order: 10 },
        { id: 'updatedAt', label: 'Updated At', visible: false, order: 11 },
        { id: 'userId', label: 'User ID', visible: false, order: 12 },
        { id: 'partnerId', label: 'Partner ID', visible: false, order: 13 },
        { id: 'countryId', label: 'Country ID', visible: false, order: 14 },
        { id: 'cityId', label: 'City ID', visible: false, order: 15 },
        { id: 'userEmail', label: 'Customer Email', visible: false, order: 16 },
        { id: 'userPhone', label: 'Customer Phone', visible: false, order: 17 },
        { id: 'userType', label: 'Customer Type', visible: false, order: 18 },
        {
            id: 'userCountryId',
            label: 'Customer Country ID',
            visible: false,
            order: 19,
        },
        {
            id: 'partnerLogoUrl',
            label: 'Partner Logo',
            visible: false,
            order: 20,
        },
        {
            id: 'partnerCountryId',
            label: 'Partner Country ID',
            visible: false,
            order: 21,
        },
        {
            id: 'partnerUserId',
            label: 'Partner User ID',
            visible: false,
            order: 22,
        },
        { id: 'deletedAt', label: 'Deleted At', visible: false, order: 23 },
        { id: 'delete', label: 'Delete', visible: false, order: 24 },
    ];

    // Function to render column based on column ID
    const renderColumn = (columnId: string): ReactNode => {
        switch (columnId) {
            case 'serial':
                return (
                    <TextField
                        key={columnId}
                        source="serial"
                        sortable={false}
                    />
                );
            case 'id':
                return <TextField key={columnId} source="id" />;
            case 'name':
                return <TextField key={columnId} source="name" />;
            case 'line1':
                return <TextField key={columnId} source="line1" label="Line" />;
            case 'area':
                return <TextField key={columnId} source="area" />;
            case 'customer':
                return (
                    <FunctionField
                        key={columnId}
                        label="Customer"
                        render={record => <CustomerField record={record} />}
                    />
                );
            case 'partner':
                return (
                    <FunctionField
                        key={columnId}
                        label="Partner"
                        render={record => <PartnerField record={record} />}
                    />
                );
            case 'city':
                return (
                    <TextField key={columnId} source="city.name" label="City" />
                );
            case 'country':
                return (
                    <TextField
                        key={columnId}
                        source="country.name"
                        label="Country"
                    />
                );
            case 'createdAt':
                return (
                    <DateField
                        key={columnId}
                        source="createdAt"
                        label="Created At"
                        locales="en-GB"
                        options={{
                            year: 'numeric',
                            month: '2-digit',
                            day: '2-digit',
                            hour: '2-digit',
                            minute: '2-digit',
                        }}
                    />
                );
            case 'updatedAt':
                return (
                    <DateField
                        key={columnId}
                        source="updatedAt"
                        label="Updated At"
                        locales="en-GB"
                        options={{
                            year: 'numeric',
                            month: '2-digit',
                            day: '2-digit',
                            hour: '2-digit',
                            minute: '2-digit',
                        }}
                    />
                );
            case 'userId':
                return (
                    <TextField key={columnId} source="userId" label="User ID" />
                );
            case 'partnerId':
                return (
                    <TextField
                        key={columnId}
                        source="partnerId"
                        label="Partner ID"
                    />
                );
            case 'countryId':
                return (
                    <TextField
                        key={columnId}
                        source="countryId"
                        label="Country ID"
                    />
                );
            case 'cityId':
                return (
                    <TextField key={columnId} source="cityId" label="City ID" />
                );
            case 'userEmail':
                return (
                    <TextField
                        key={columnId}
                        source="user.email"
                        label="Customer Email"
                    />
                );
            case 'userPhone':
                return (
                    <TextField
                        key={columnId}
                        source="user.phone"
                        label="Customer Phone"
                    />
                );
            case 'userType':
                return (
                    <FunctionField
                        key={columnId}
                        label="Customer Type"
                        render={record => <CustomerTypeField record={record} />}
                    />
                );
            case 'userCountryId':
                return (
                    <TextField
                        key={columnId}
                        source="user.countryId"
                        label="Customer Country ID"
                    />
                );
            case 'partnerLogoUrl':
                return (
                    <TextField
                        key={columnId}
                        source="partner.logoUrl"
                        label="Partner Logo URL"
                    />
                );
            case 'partnerCountryId':
                return (
                    <TextField
                        key={columnId}
                        source="partner.countryId"
                        label="Partner Country ID"
                    />
                );
            case 'partnerUserId':
                return (
                    <TextField
                        key={columnId}
                        source="partner.userId"
                        label="Partner User ID"
                    />
                );
            case 'deletedAt':
                return (
                    <DateField
                        key={columnId}
                        source="deletedAt"
                        label="Deleted At"
                        locales="en-GB"
                        options={{
                            year: 'numeric',
                            month: '2-digit',
                            day: '2-digit',
                            hour: '2-digit',
                            minute: '2-digit',
                        }}
                    />
                );
            case 'delete':
                return <DeleteButton key={columnId} undoable={false} />;
            case 'show':
                return <ShowButton key={columnId} />;
            default:
                return null;
        }
    };

    // Ensure we have all required props, but avoid duplicates with what might be in props
    const combinedProps = {
        title: 'Addresses',
        permissions,
        defaultColumns,
        storageKey: 'addressListColumns',
        renderColumn,
        filtersComponent: <AddressFilter />,
        sort: { field: 'id', order: 'DESC' },
        paginationComponent: <BoxitPagination />,
        perPage: 20,
        ...props,
        // Ensure these are set if not in props
        resource: props.resource || 'addresses',
        location: props.location || { search: '' },
        hasShow: props.hasShow !== undefined ? props.hasShow : true,
    };

    return <CustomizableGrid {...combinedProps} />;
};

AddressList.defaultProps = {
    resource: 'addresses',
    location: { search: '' },
};

export default AddressList;
